// A04: per-workspace Model Access panel. Three peer paths:
//   openrouter           — covered by the org's OpenRouter provider key
//                          (set in the Provider Keys panel above).
//   claude_subscription  — Captain's Claude Pro/Max subscription,
//                          authenticated via `claude login` inside the
//                          sandbox.
//   codex_subscription   — Captain's ChatGPT Plus/Pro subscription,
//                          authenticated via `codex`'s Sign-in-with-
//                          ChatGPT flow inside the sandbox.
//
// Panel shows one chip per provider with its current status and a
// Connect / Revoke button. Clicking Connect flips status to "pending"
// and instructs the Captain to run the CLI login in the sandbox;
// when the CLI confirms, a backend hook flips it to "connected".
// (The hook itself is the next step — for now the Captain can bump
// status manually by re-Connecting once login is done.)

import { useEffect, useMemo, useState } from "react";
import {
  ApiError,
  extractRows,
  listModelAccess,
  revokeModelAccess,
  setModelAccess,
  type ModelAccessProvider,
  type ModelAccessStatus,
} from "../api/client";

interface Props {
  workspaceId: number;
}

interface ProviderMeta {
  id: ModelAccessProvider;
  label: string;
  hint: string;
  connectHint: string;
}

const PROVIDERS: ProviderMeta[] = [
  {
    id: "openrouter",
    label: "OpenRouter",
    hint: "Default BYOK path. Uses the OpenRouter key set in the Provider Keys panel.",
    connectHint:
      "Paste an OpenRouter key in the Provider Keys panel above, then click Connect.",
  },
  {
    id: "claude_subscription",
    label: "Claude subscription",
    hint: "Claude Pro/Max covers the working agent's turns when a persona backs on Anthropic.",
    connectHint:
      "In the workspace sandbox, run `claude login` (wake the sandbox from the chrome first). Then click Connect to mark it ready.",
  },
  {
    id: "codex_subscription",
    label: "Codex subscription",
    hint: "ChatGPT Plus/Pro covers the agent when a persona backs on OpenAI.",
    connectHint:
      "In the workspace sandbox, run `codex login` (Sign in with ChatGPT). Then click Connect to mark it ready.",
  },
];

function toneFor(status: ModelAccessStatus | null): string {
  switch (status) {
    case "connected":
      return "border-accent text-accent";
    case "pending":
      return "border-warning text-warning";
    case "needs_reauth":
      return "border-danger text-danger";
    case "revoked":
      return "border-edge text-faint";
    default:
      return "border-edge text-faint";
  }
}

function labelFor(status: ModelAccessStatus | null): string {
  switch (status) {
    case "connected":
      return "connected";
    case "pending":
      return "pending login";
    case "needs_reauth":
      return "needs reauth";
    case "revoked":
      return "revoked";
    default:
      return "not connected";
  }
}

export function ModelAccessPanel({ workspaceId }: Props) {
  const [statusByProvider, setStatusByProvider] = useState<
    Map<ModelAccessProvider, ModelAccessStatus>
  >(new Map());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [focused, setFocused] = useState<ModelAccessProvider | null>(null);

  async function refresh() {
    try {
      const res = await listModelAccess(workspaceId);
      const rows = extractRows(res);
      const next = new Map<ModelAccessProvider, ModelAccessStatus>();
      for (const row of rows) {
        // Columns: access_id, workspace_id, provider, status, last_verified_at, updated_at.
        const provider = typeof row[2] === "string" ? row[2] : "";
        const status = typeof row[3] === "string" ? row[3] : "";
        if (!provider || !status) continue;
        next.set(
          provider as ModelAccessProvider,
          status as ModelAccessStatus,
        );
      }
      setStatusByProvider(next);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not load model access",
      );
    }
  }

  useEffect(() => {
    if (workspaceId > 0) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  async function onConnect(p: ModelAccessProvider) {
    setBusy(true);
    setError("");
    try {
      // "openrouter" flips straight to connected (the provider key is
      // org-level, already managed). Subscription paths flip to
      // "connected" on click — the Captain is asserting they've run
      // the CLI login. If the loop then fails, the CLI error surfaces
      // and the Captain can revoke + retry.
      await setModelAccess(workspaceId, p, "connected");
      await refresh();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : `Could not mark ${p} as connected`,
      );
    } finally {
      setBusy(false);
    }
  }

  async function onRevoke(p: ModelAccessProvider) {
    setBusy(true);
    setError("");
    try {
      await revokeModelAccess(workspaceId, p);
      await refresh();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : `Could not revoke ${p}`,
      );
    } finally {
      setBusy(false);
    }
  }

  const rendered = useMemo(() => PROVIDERS, []);

  return (
    <section
      className="flex flex-col gap-3 rounded-panel border border-edge bg-surface-glass p-4"
      data-testid="model-access-panel"
    >
      <header>
        <h3 className="text-sm font-semibold text-primary">Model access</h3>
        <p className="mt-1 text-xs text-muted">
          Which path each persona can back on. Flip a persona's model_backing
          in the persona editor once a provider shows as connected.
        </p>
      </header>
      <ul className="flex flex-col gap-2">
        {rendered.map((meta) => {
          const status = statusByProvider.get(meta.id) ?? null;
          const isFocused = focused === meta.id;
          const connected = status === "connected";
          return (
            <li
              key={meta.id}
              className={`rounded-md border ${toneFor(status)} bg-surface-raised px-3 py-2`}
              data-testid={`model-access-${meta.id}`}
            >
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-xs font-medium text-primary">
                  {meta.label}
                </span>
                <span className="font-code text-[0.625rem]">
                  {labelFor(status)}
                </span>
              </div>
              <p className="mt-1 text-[0.6875rem] text-muted">{meta.hint}</p>
              {isFocused && (
                <p className="mt-2 text-[0.6875rem] text-muted">
                  {meta.connectHint}
                </p>
              )}
              <div className="mt-2 flex gap-2">
                {connected ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void onRevoke(meta.id)}
                    className="cursor-pointer rounded-md border border-edge bg-surface-glass px-2 py-1 text-[0.6875rem] text-muted transition-colors hover:border-danger hover:text-danger disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Revoke
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={busy}
                    onFocus={() => setFocused(meta.id)}
                    onBlur={() => setFocused(null)}
                    onClick={() => void onConnect(meta.id)}
                    className="cursor-pointer rounded-md border border-edge bg-surface-glass px-2 py-1 text-[0.6875rem] text-primary transition-colors hover:border-accent disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Connect
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {error && (
        <p className="text-xs text-danger" data-testid="model-access-error">
          {error}
        </p>
      )}
    </section>
  );
}
